/**
 * Gestión de Contratos de Empleado
 * Fecha: 16 Oct 2025
 * 
 * Vista completa para gestión de contratos con timeline, generación desde plantillas,
 * carga de PDFs firmados y acciones de activar/terminar
 */
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { contractsService } from '@/features/rh/api/contracts.service';
import { contractTemplatesService } from '@/features/rh/api/contractTemplates.service';
import { employeesService } from '@/features/rh/api/employees.service';
import type {
  Contract,
  ContractTemplate,
  Employee,
  ContractStatus,
} from '@/types/employee.types';
import {
  LoadingSpinner,
  Badge,
  Modal,
  PermissionGuard,
} from '@/shared/components';
import type { ColorDeBadge } from '@/shared/components/Badge';
import {
  ArrowLeft,
  FileText,
  Plus,
  Upload,
  CheckCircle,
  XCircle,
  Calendar,
  Eye,
  Download,
  PlayCircle,
  StopCircle,
  Clock,
  FileCheck,
  AlertTriangle,
} from 'lucide-react';
import { usePermissions, EMPLOYEE_PERMISSIONS } from '@/shared/hooks/usePermissions';
import toast from 'react-hot-toast';
import KpiCard from '@/features/rh/components/dashboard/KpiCard';

export const EmployeeContracts: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { hasPermission } = usePermissions();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Generate Contract Wizard
  const [showGenerateWizard, setShowGenerateWizard] = useState(false);
  const [generateStep, setGenerateStep] = useState(1);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ContractTemplate | null>(null);
  const [previewContent, setPreviewContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  // Upload Signed Modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadContractId, setUploadContractId] = useState<number | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Activate Modal
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [activateContract, setActivateContract] = useState<Contract | null>(null);
  const [activateFechaInicio, setActivateFechaInicio] = useState('');
  const [activateFechaFin, setActivateFechaFin] = useState('');
  const [isActivating, setIsActivating] = useState(false);

  // Terminate Modal
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [terminateContract, setTerminateContract] = useState<Contract | null>(null);
  const [terminateFechaFin, setTerminateFechaFin] = useState('');
  const [terminateMotivo, setTerminateMotivo] = useState('');
  const [isTerminating, setIsTerminating] = useState(false);

  // const canGenerate = hasPermission(EMPLOYEE_PERMISSIONS.CONTRACTS_GENERATE);
  const canUpload = hasPermission(EMPLOYEE_PERMISSIONS.CONTRACTS_CREATE);
  const canActivate = hasPermission(EMPLOYEE_PERMISSIONS.CONTRACTS_UPDATE);
  const canTerminate = hasPermission(EMPLOYEE_PERMISSIONS.CONTRACTS_DELETE);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [employeeData, contractsData, templatesData] = await Promise.all([
        employeesService.getById(Number(id)),
        contractsService.getAll({ employeeId: Number(id) }),
        contractTemplatesService.getAll(),
      ]);
      setEmployee(employeeData);
      setContracts(contractsData);
      setTemplates(templatesData.filter((t: ContractTemplate) => t.activo));
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar datos:', err);
      toast.error(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate Contract Wizard
  const handleStartGenerate = () => {
    setGenerateStep(1);
    setSelectedTemplateId(null);
    setSelectedTemplate(null);
    setPreviewContent('');
    setShowGenerateWizard(true);
  };

  const handleTemplateSelect = (templateId: number) => {
    const template = templates.find((t) => t.id === templateId);
    setSelectedTemplateId(templateId);
    setSelectedTemplate(template || null);
  };

  const handleNextStep = async () => {
    if (generateStep === 1) {
      if (!selectedTemplateId || !employee) {
        toast.error('Selecciona una plantilla');
        return;
      }
      // Generate simple preview (without backend call)
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (template) {
        let preview = template.contenidoPlantilla;
        // Simple variable replacement for preview
        const variables = {
          nombre: `${employee.user?.firstName} ${employee.user?.lastName}`,
          curp: employee.curp,
          rfc: employee.rfc,
          nss: employee.nss || '-',
          puesto: employee.puesto?.nombre || '-',
          area: employee.area?.nombre || '-',
          sueldo: employee.sueldoBrutoMensual.toString(),
          fechaIngreso: new Date(employee.fechaIngreso).toLocaleDateString('es-MX'),
        };
        Object.entries(variables).forEach(([key, value]) => {
          preview = preview.replace(new RegExp(`{{${key}}}`, 'g'), value);
        });
        setPreviewContent(preview);
      }
      setGenerateStep(2);
    } else if (generateStep === 2) {
      setGenerateStep(3);
    }
  };

  const handlePrevStep = () => {
    if (generateStep > 1) {
      setGenerateStep(generateStep - 1);
    }
  };

  const handleGenerateSubmit = async () => {
    if (!selectedTemplateId) return;
    try {
      setIsGenerating(true);
      await contractsService.generate({
        employeeId: Number(id),
        templateId: selectedTemplateId,
      });
      await loadData();
      setShowGenerateWizard(false);
      setGenerateStep(1);
      setSelectedTemplateId(null);
      setSelectedTemplate(null);
      setPreviewContent('');
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al generar contrato:', err);
      toast.error(err.message || 'Error al generar contrato');
    } finally {
      setIsGenerating(false);
    }
  };

  // Upload Signed PDF
  const handleUploadClick = (contractId: number) => {
    setUploadContractId(contractId);
    setUploadFile(null);
    setShowUploadModal(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadContractId || !uploadFile) {
      toast.error('Selecciona un archivo');
      return;
    }

    try {
      setIsUploading(true);
      await contractsService.uploadSigned(uploadContractId, uploadFile);
      await loadData();
      setShowUploadModal(false);
      setUploadContractId(null);
      setUploadFile(null);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar contrato firmado:', err);
      toast.error(err.message || 'Error al cargar contrato firmado');
    } finally {
      setIsUploading(false);
    }
  };

  // Activate Contract
  const handleActivateClick = (contract: Contract) => {
    setActivateContract(contract);
    setActivateFechaInicio(new Date().toISOString().split('T')[0]);
    setActivateFechaFin('');
    setShowActivateModal(true);
  };

  const handleActivateSubmit = async () => {
    if (!activateContract || !activateFechaInicio) {
      toast.error('La fecha de inicio es obligatoria');
      return;
    }

    try {
      setIsActivating(true);
      // First update with dates
      await contractsService.update(activateContract.id, {
        fechaInicio: activateFechaInicio,
        fechaFin: activateFechaFin || null,
      });
      // Then activate
      await contractsService.activate(activateContract.id);
      await loadData();
      setShowActivateModal(false);
      setActivateContract(null);
      setActivateFechaInicio('');
      setActivateFechaFin('');
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al activar contrato:', err);
      toast.error(err.message || 'Error al activar contrato');
    } finally {
      setIsActivating(false);
    }
  };

  // Terminate Contract
  const handleTerminateClick = (contract: Contract) => {
    setTerminateContract(contract);
    setTerminateFechaFin(new Date().toISOString().split('T')[0]);
    setTerminateMotivo('');
    setShowTerminateModal(true);
  };

  const handleTerminateSubmit = async () => {
    if (!terminateContract || !terminateFechaFin) {
      toast.error('La fecha de fin y motivo son obligatorios');
      return;
    }

    try {
      setIsTerminating(true);
      // First update with termination date
      await contractsService.update(terminateContract.id, {
        fechaFin: terminateFechaFin,
      });
      // Then terminate
      await contractsService.terminate(terminateContract.id);
      await loadData();
      setShowTerminateModal(false);
      setTerminateContract(null);
      setTerminateFechaFin('');
      setTerminateMotivo('');
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al terminar contrato:', err);
      toast.error(err.message || 'Error al terminar contrato');
    } finally {
      setIsTerminating(false);
    }
  };

  const handleDownload = (contract: Contract) => {
    if (contract.urlContratoFirmado) {
      window.open(contract.urlContratoFirmado, '_blank');
    } else if (contract.urlContratoGenerado) {
      window.open(contract.urlContratoGenerado, '_blank');
    }
  };

  const handleView = (contract: Contract) => {
    if (contract.urlContratoFirmado) {
      window.open(contract.urlContratoFirmado, '_blank');
    } else if (contract.urlContratoGenerado) {
      window.open(contract.urlContratoGenerado, '_blank');
    }
  };

  const getContractsByStatus = (status: ContractStatus): Contract[] => {
    return contracts.filter((c) => c.status === status);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Cargando contratos..." />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-6 shadow-sm">
            <h3 className="text-lg font-bold text-red-700 mb-2">
              Error al cargar empleado
            </h3>
            <button
              onClick={() => navigate('/employees')}
              className={estiloDeBoton('primario')}
            >
              Volver a la lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  const activeContracts = getContractsByStatus('activo');
  const generatedContracts = getContractsByStatus('generado');
  const signedContracts = getContractsByStatus('firmado');
  const terminatedContracts = getContractsByStatus('terminado');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/employees/${id}`)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al detalle del empleado
          </button>
          <div className="flex items-center justify-between">
            <div>
              <TituloDePantalla nivel="interna" className="mb-2">
                Contratos
              </TituloDePantalla>
              <p className="text-gray-600 dark:text-gray-400">
                {employee.user?.firstName} {employee.user?.lastName}
              </p>
            </div>
            <PermissionGuard permission={EMPLOYEE_PERMISSIONS.CONTRACTS_GENERATE}>
              <button
                onClick={handleStartGenerate}
                className={estiloDeBoton('primario')}
              >
                <Plus className="w-4 h-4" />
                Generar Contrato
              </button>
            </PermissionGuard>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <KpiCard title="Total" value={contracts.length} subtext="contratos" icon={<FileText />} variant="primary" />
          <KpiCard title="Activos" value={activeContracts.length} subtext="vigentes" icon={<CheckCircle />} variant="success" />
          <KpiCard title="Pendientes" value={generatedContracts.length + signedContracts.length} subtext="por activar" icon={<Clock />} variant="warning" />
          <KpiCard title="Terminados" value={terminatedContracts.length} subtext="finalizados" icon={<XCircle />} variant="error" />
        </div>

        {/* Contracts List */}
        {contracts.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No hay contratos registrados
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Genera el primer contrato para este empleado
            </p> 
          </div>
        ) : (
          <div className="space-y-6">
            {/* Active Contracts */}
            {activeContracts.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Contratos Activos ({activeContracts.length})
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {activeContracts.map((contract) => (
                    <ContractCard
                      key={contract.id}
                      contract={contract}
                      onView={handleView}
                      onDownload={handleDownload}
                      onTerminate={canTerminate ? handleTerminateClick : undefined}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Signed Contracts (pending activation) */}
            {signedContracts.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-yellow-600" />
                  Contratos Firmados - Pendientes de Activar ({signedContracts.length})
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {signedContracts.map((contract) => (
                    <ContractCard
                      key={contract.id}
                      contract={contract}
                      onView={handleView}
                      onDownload={handleDownload}
                      onActivate={canActivate ? handleActivateClick : undefined}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Generated Contracts (pending signature) */}
            {generatedContracts.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Contratos Generados - Pendientes de Firma ({generatedContracts.length})
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {generatedContracts.map((contract) => (
                    <ContractCard
                      key={contract.id}
                      contract={contract}
                      onView={handleView}
                      onDownload={handleDownload}
                      onUploadSigned={canUpload ? handleUploadClick : undefined}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Terminated Contracts */}
            {terminatedContracts.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-600" />
                  Contratos Terminados ({terminatedContracts.length})
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {terminatedContracts.map((contract) => (
                    <ContractCard
                      key={contract.id}
                      contract={contract}
                      onView={handleView}
                      onDownload={handleDownload}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Generate Contract Wizard */}
      <Modal
        isOpen={showGenerateWizard}
        onClose={() => setShowGenerateWizard(false)}
        title="Generar Contrato"
        size="xl"
      >
        <div className="space-y-6">
          {/* Steps Indicator */}
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center flex-1">
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                    generateStep >= step
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400'
                  }`}
                >
                  {step}
                </div>
                {step < 3 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      generateStep > step ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Select Template */}
          {generateStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Paso 1: Seleccionar Plantilla
              </h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {templates.filter((t) => t.tipoContrato === employee.tipoContrato).length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-3" />
                    <p>No hay plantillas disponibles para el tipo de contrato: {employee.tipoContrato}</p>
                  </div>
                ) : (
                  templates
                    .filter((t) => t.tipoContrato === employee.tipoContrato)
                    .map((template) => (
                      <div
                        key={template.id}
                        onClick={() => handleTemplateSelect(template.id)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          selectedTemplateId === template.id
                            ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white">
                              {template.nombre}
                            </h4>
                            <Badge color="morado" className="mt-2">
                              {template.tipoContrato}
                            </Badge>
                          </div>
                          {selectedTemplateId === template.id && (
                            <CheckCircle className="w-6 h-6 text-blue-600" />
                          )}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {generateStep === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Paso 2: Vista Previa
              </h3>
              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-6 bg-white dark:bg-gray-900 max-h-96 overflow-y-auto">
                <div
                  className="prose dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewContent }}
                />
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {generateStep === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Paso 3: Confirmar Generación
              </h3>
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-6 shadow-sm">
                <div className="flex items-start gap-3">
                  <FileText className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-blue-700 mb-2">
                      ¿Confirmar generación de contrato?
                    </h4>
                    <p className="text-sm text-blue-600 font-semibold leading-relaxed mb-3">
                      Se generará un contrato basado en la plantilla: <strong>{selectedTemplate?.nombre}</strong>
                    </p>
                    <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                      <li>• El contrato se generará en formato PDF</li>
                      <li>• Podrás descargarlo para firma</li>
                      <li>• Después de firmado, podrás cargar el PDF firmado</li>
                      <li>• Finalmente podrás activarlo para que entre en vigencia</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Wizard Footer */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={() => setShowGenerateWizard(false)}
            className={estiloDeBoton('secundario')}
            disabled={isGenerating}
          >
            Cancelar
          </button>
          <div className="flex gap-3">
            {generateStep > 1 && (
              <button
                onClick={handlePrevStep}
                className={estiloDeBoton('secundario')}
                disabled={isGenerating}
              >
                Anterior
              </button>
            )}
            {generateStep < 3 ? (
              <button
                onClick={handleNextStep}
                disabled={generateStep === 1 && !selectedTemplateId}
                className={estiloDeBoton('primario')}
              >
                Siguiente
              </button>
            ) : (
              <button
                onClick={handleGenerateSubmit}
                disabled={isGenerating}
                className={estiloDeBoton('primario')}
              >
                {isGenerating && <LoadingSpinner size="sm" />}
                Generar Contrato
              </button>
            )}
          </div>
        </div>
      </Modal>

      {/* Upload Signed Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="Cargar Contrato Firmado"
      >
        <div className="space-y-4">
          <div className="bg-orange-50 border-l-4 border-orange-500 rounded-r-lg p-4 shadow-sm">
            <p className="text-sm text-orange-700 font-semibold leading-relaxed">
              Carga el contrato firmado en formato PDF para proceder con su activación.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Archivo PDF Firmado *
            </label>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept=".pdf"
              className="block w-full text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-white dark:bg-gray-800 focus:outline-none"
            />
            {uploadFile && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Archivo seleccionado: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => setShowUploadModal(false)}
            className={estiloDeBoton('secundario')}
            disabled={isUploading}
          >
            Cancelar
          </button>
          <button
            onClick={handleUploadSubmit}
            disabled={isUploading || !uploadFile}
            className={estiloDeBoton('primario')}
          >
            {isUploading && <LoadingSpinner size="sm" />}
            <Upload className="w-4 h-4" />
            Cargar Contrato
          </button>
        </div>
      </Modal>

      {/* Activate Contract Modal */}
      <Modal
        isOpen={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        title="Activar Contrato"
      >
        {activateContract && (
          <div className="space-y-4">
            <div className="bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4 shadow-sm">
              <p className="text-sm text-green-700 font-semibold leading-relaxed">
                Al activar el contrato, entrará en vigencia y será considerado el contrato activo del empleado.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fecha de Inicio *
              </label>
              <input
                type="date"
                value={activateFechaInicio}
                onChange={(e) => setActivateFechaInicio(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fecha de Fin (opcional)
              </label>
              <input
                type="date"
                value={activateFechaFin}
                onChange={(e) => setActivateFechaFin(e.target.value)}
                min={activateFechaInicio}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Deja vacío para contratos por tiempo indeterminado
              </p>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => setShowActivateModal(false)}
            className={estiloDeBoton('secundario')}
            disabled={isActivating}
          >
            Cancelar
          </button>
          <button
            onClick={handleActivateSubmit}
            disabled={isActivating || !activateFechaInicio}
            className={estiloDeBoton('exito')}
          >
            {isActivating && <LoadingSpinner size="sm" />}
            <PlayCircle className="w-4 h-4" />
            Activar Contrato
          </button>
        </div>
      </Modal>

      {/* Terminate Contract Modal */}
      <Modal
        isOpen={showTerminateModal}
        onClose={() => setShowTerminateModal(false)}
        title="Terminar Contrato"
      >
        {terminateContract && (
          <div className="space-y-4">
            <div className="bg-orange-50 border-l-4 border-orange-500 rounded-r-lg p-4 shadow-sm">
              <p className="text-sm text-orange-700 font-semibold leading-relaxed">
                Al terminar el contrato, dejará de estar activo y no se podrá reactivar.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fecha de Terminación *
              </label>
              <input
                type="date"
                value={terminateFechaFin}
                onChange={(e) => setTerminateFechaFin(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Motivo de Terminación (opcional)
              </label>
              <textarea
                value={terminateMotivo}
                onChange={(e) => setTerminateMotivo(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Describe el motivo de la terminación del contrato..."
              />
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => setShowTerminateModal(false)}
            className={estiloDeBoton('secundario')}
            disabled={isTerminating}
          >
            Cancelar
          </button>
          <button
            onClick={handleTerminateSubmit}
            disabled={isTerminating || !terminateFechaFin}
            className={estiloDeBoton('peligro')}
          >
            {isTerminating && <LoadingSpinner size="sm" />}
            <StopCircle className="w-4 h-4" />
            Terminar Contrato
          </button>
        </div>
      </Modal>
    </div>
  );
};

// Contract Card Component
interface ContractCardProps {
  contract: Contract;
  onView: (contract: Contract) => void;
  onDownload: (contract: Contract) => void;
  onUploadSigned?: (contractId: number) => void;
  onActivate?: (contract: Contract) => void;
  onTerminate?: (contract: Contract) => void;
}

const ContractCard: React.FC<ContractCardProps> = ({
  contract,
  onView,
  onDownload,
  onUploadSigned,
  onActivate,
  onTerminate,
}) => {
  const getStatusBadgeColor = (status: ContractStatus): ColorDeBadge => {
    switch (status) {
      case 'activo':
        return 'verde';
      case 'generado':
        return 'azul';
      case 'firmado':
        return 'verde';
      case 'terminado':
        return 'rojo';
      default:
        return 'gris';
    }
  };

  const getStatusIcon = (status: ContractStatus) => {
    switch (status) {
      case 'activo':
        return CheckCircle;
      case 'generado':
        return FileText;
      case 'firmado':
        return FileCheck;
      case 'terminado':
        return XCircle;
      default:
        return Clock;
    }
  };

  const StatusIcon = getStatusIcon(contract.status);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <StatusIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Contrato #{contract.id}
            </h3>
            <Badge color={getStatusBadgeColor(contract.status)} className="mt-1">
              {contract.status}
            </Badge>
          </div>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Calendar className="w-4 h-4" />
          <span>
            {contract.fechaInicio
              ? new Date(contract.fechaInicio).toLocaleDateString('es-MX')
              : 'Sin fecha de inicio'}
            {contract.fechaFin && ` - ${new Date(contract.fechaFin).toLocaleDateString('es-MX')}`}
            {!contract.fechaFin && contract.status === 'activo' && ' - Indeterminado'}
          </span>
        </div>

        {contract.template && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Plantilla:</span> {contract.template.nombre}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {(contract.urlContratoGenerado || contract.urlContratoFirmado) && (
          <>
            <button
              onClick={() => onView(contract)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <Eye className="w-4 h-4" />
              Ver
            </button>
            <button
              onClick={() => onDownload(contract)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <Download className="w-4 h-4" />
              Descargar
            </button>
          </>
        )}

        {contract.status === 'generado' && onUploadSigned && (
          <button
            onClick={() => onUploadSigned(contract.id)}
            className={estiloDeBoton('primario')}
          >
            <Upload className="w-4 h-4" />
            Cargar Firmado
          </button>
        )}

        {contract.status === 'firmado' && onActivate && (
          <button
            onClick={() => onActivate(contract)}
            className={estiloDeBoton('exito')}
          >
            <PlayCircle className="w-4 h-4" />
            Activar
          </button>
        )}

        {contract.status === 'activo' && onTerminate && (
          <button
            onClick={() => onTerminate(contract)}
            className={estiloDeBoton('peligro')}
          >
            <StopCircle className="w-4 h-4" />
            Terminar
          </button>
        )}
      </div>
    </div>
  );
};
